import type { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetColumn } from "./sheet-column.ts";
import { PartialDate } from "@tento-chrono";
import { ColumnHeader } from "../plugins/shared/column-header/cell.ts";

/**
 * A placed column's header label: the weekday and day number of each day in
 * its range. A pure function of the range, so it is written at placement and
 * the bind that follows never has to correct it.
 */
function headerLabels(start: HeaderRangeSource.Day, days: number): string[] {
  return Array.from({ length: days }, (_, i) => {
    const day = start.addDays(i);
    return `${day.dayOfWeek.shortname} ${day.day}`;
  });
}

/**
 * A placed header's label, the same text its bind writes: a day column reads
 * its weekday and day number; a week, month or year column reads its card's
 * own text (the range and "week N", the range and the month name, the year)
 * in the date format its card was last bound with.
 */
function headerTexts(date: HeaderRangeSource.Range, days: number, $header: Element): string[] {
  if (date.type === "day" || !(date instanceof PartialDate)) return headerLabels(date.start, days);
  const label = ColumnHeader.label(date, ColumnHeader.boundDateFormat($header));
  return [label.value, label.caption];
}

/**
 * What a header's label reads from its sheet: the date range of the column
 * at a key. A sheet satisfies it; so does any source that maps a key to a
 * range, which keeps the header's skeleton independent of the sheet's other
 * state.
 */
export interface HeaderRangeSource {
  readonly indexRenderer: {
    readonly mapper: {
      resolveKey(key: ColumnIndex.Key): {
        castToDefault(): { discrete(): readonly [HeaderRangeSource.Range, ...unknown[]] } | null | undefined;
      };
    };
  };
}

/** The parts of a column range a header label reads. */
export declare namespace HeaderRangeSource {
  /** A day of a column's range, as far as its label reads it. */
  type Day = { addDays(n: number): { dayOfWeek: { shortname: string }; day: number } };
  /** A column's range (the first entry of its discrete dates): its kind and first day. */
  type Range = { readonly type: string; readonly start: Day };
}

/** A placed header's skeleton: its range's label, never events or cell renderers. */
export class ColumnPlaceholder {
  /**
   * The header's skeleton, written the moment its bound content goes stale
   * (placed at another index, or marked `pending-layout` by a rebind): the
   * label for the range at `key` (its stale cells hidden until the bind). A
   * header whose content is current is left alone; the body and its cells
   * are untouched.
   */
  static labelHeader(
    sheet: HeaderRangeSource,
    key: ColumnIndex.Key,
    $header: HTMLElement,
  ): void {
    if (!($header instanceof SheetColumn)) return;
    if (!$header.hasAttribute("placed-stale") && !$header.hasAttribute("pending-layout")) return;
    const column = sheet.indexRenderer.mapper.resolveKey(key).castToDefault();
    if (!column) return; // Custom flight columns own their geometry and preview.
    const date = column.discrete()[0];
    const days = date.type === "week" ? 7 : 1;
    $header.showPlaceholder(headerTexts(date, days, $header));
  }
}
